import { BaseExecutable } from "../Common/Execution/BaseExecutable";
import { IDesignValidationProvider, ValidationIssue } from "../Common/IDesignValidationProvider";
import { DataUtilities } from "../Common/DataUtilities";
import { FlowStepSequence } from "./Structure/FlowStepSequence";
import { FlowOutputDataStrategy } from "./Structure/FlowOutputDataStrategy";
import { FlowDataFetchType, FlowUtilities, StepNodes } from "./Utilities/FlowUtilities";
import { CandidateSequence } from "./Structure/CandidateSequence";
import { StateManager } from "../Common/StateManager";
import { FlowStepData } from "./Steps/FlowStepData";
import { IFlowStepSequence } from "./Structure/IFlowStepSequence";
import { FlowConnection } from "./Structure/FlowConnection";
import { FlowStepOutputInstructions, FlowStepOutputInstructionType } from "./Structure/FlowStepOutputInstructions";
import { FlowSequenceCompleteStrategy } from "./Structure/FlowSequenceCompleteStrategy";
import { MultiExecutableOutputDefinition } from "../Common/Execution/MultiExecutable";
import { IDataDefinition } from "../Common/Data/Definitions/IDataDefinition";
import { FlowConstants } from "./Utilities/FlowConstants";
import { FlowStepDataRegistration } from "./Utilities/RegisterFlowStepData";
import { DataDefinition } from "../Common/Data/DataDefinition";
import { MappingConfiguration } from "../Mapping/MappingConfiguration";
import { AppGlueCommonInit } from "../AppGlueInit";

export enum FlowOutputType {
    NODATA = "none",
    ALLDATA = "all",
    ADDEDDATA = "added",
    DEFINED = "defined",
}

export class FlowConfiguration extends BaseExecutable implements IDesignValidationProvider {
    static ADD_SEQUENCE = "add_sequence";
    static REMOVE_SEQUENCE = "remove_sequence";
    static COLLAPSED_CHANGED = "collapsed_changed";
    static VISIBLE_STEPS: string = "visibleSteps";
    static FAVORITE_STEPS: string = "favoriteSteps";
    static CANDIDATE_SEQUENCES: string = "candidateSequences";
    static SEQUENCES: string = "sequences";
    static OUTPUT_STRATEGY: string = "outputStrategy";
    _id: string = DataUtilities.generateUniqueId();
    errorSequence: FlowStepSequence;
    endSequence: FlowStepSequence;
    visibleSteps: string[] = [];
    favoriteSteps: string[] = [];
    flowDataOutputStrategy: FlowOutputDataStrategy = FlowOutputDataStrategy.ChangedData;
    flowOutputVariableNames: string[] = [];

    constructor() {
        super();

        // ensure flow steps are registered
        AppGlueCommonInit.init();

        this._sequences = [];
        let initialSeq = new FlowStepSequence(FlowConstants.DEFAULT_PRIMARY_SEQUENCE_COLOR);
        initialSeq.desiredX = FlowConstants.PRIMARY_SEQUENCE_POSITION.x;
        initialSeq.desiredY = FlowConstants.PRIMARY_SEQUENCE_POSITION.y;
        initialSeq._id = FlowConstants.PRIMARY_SEQUENCE;

        this.addSequence(initialSeq);

        this.endSequence = new FlowStepSequence(FlowConstants.DEFAULT_END_SEQUENCE_COLOR);
        this.endSequence._id = FlowConstants.END_SEQUENCE;
        this.endSequence.isCollapsed = true;
        this.endSequence.name = "Flow End";
        this.addSequence(this.endSequence);

        this.errorSequence = new FlowStepSequence(FlowConstants.DEFAULT_ERROR_SEQUENCE_COLOR);
        this.errorSequence._id = FlowConstants.ERROR_SEQUENCE;
        this.errorSequence.isCollapsed = true;
        this.errorSequence.name = "Error";
        this.addSequence(this.errorSequence);

        this.visibleSteps = FlowStepDataRegistration.getAllRegistrationData()
            .filter((n) => {
                return n.initialStepVisible;
            })
            .map((value) => {
                return value.name;
            });
    }

    private _outputMapping: MappingConfiguration = new MappingConfiguration();

    get outputMapping(): MappingConfiguration {
        this._outputMapping.inputData = () => {
            let dd = new DataDefinition();
            dd.fields = FlowUtilities.getFlowDataDefinitions(this, FlowDataFetchType.All);
            return dd;
        };

        this._outputMapping.executionResult = () => {
            return this.getExecutionResult();
        };

        return this._outputMapping;
    }

    set outputMapping(value: MappingConfiguration) {
        this._outputMapping = value;
    }

    private _defineOutputs: FlowOutputType = FlowOutputType.NODATA;

    get defineOutputs(): FlowOutputType {
        return this._defineOutputs;
    }

    set defineOutputs(value: FlowOutputType) {
        if (this._executionResult && typeof this._executionResult === "function") {
            // do not set if function
            return;
        }

        if (value === FlowOutputType.DEFINED && !this._executionResult) {
            this._executionResult = new DataDefinition();
        }

        this._defineOutputs = value;

        StateManager.propertyChanged(this, FlowConfiguration.OUTPUT_STRATEGY);
    }

    protected _executionResult?: DataDefinition | (() => DataDefinition);

    get executionResult(): DataDefinition | (() => DataDefinition) {
        return this._executionResult!;
    }

    set executionResult(value: DataDefinition | (() => DataDefinition)) {
        this._executionResult = value;
    }

    get canChangeOutputs(): boolean {
        return !this._executionResult || !(this._executionResult instanceof Function);
    }

    private _sequences: FlowStepSequence[];

    get allSequences(): readonly FlowStepSequence[] {
        return this._sequences;
    }

    get sequences(): readonly FlowStepSequence[] {
        return this._sequences.filter((s) => {
            return s._id !== FlowConstants.END_SEQUENCE && s._id !== FlowConstants.ERROR_SEQUENCE;
        });
    }

    private _candidateSequences: CandidateSequence[] = [];

    get candidateSequences(): CandidateSequence[] {
        return this._candidateSequences;
    }

    // -- end not stored

    set candidateSequences(value: CandidateSequence[]) {
        this._candidateSequences = value;
    }

    // -- not stored
    private _nodes?: StepNodes;

    get nodes(): StepNodes {
        if (!this._nodes) this._nodes = StepNodes.buildNodes(this);
        return this._nodes;
    }

    getExecutionResult(): DataDefinition {
        if (typeof this._executionResult === "function") {
            return this._executionResult();
        }

        if (!this._executionResult) {
            this._executionResult = new DataDefinition();
        }

        return this._executionResult;
    }

    invalidateNodes() {
        this._nodes = undefined;
    }

    getPrimarySequence(): FlowStepSequence {
        return this.getSequence(FlowConstants.PRIMARY_SEQUENCE)!;
    }

    toggleShowDescription(show: boolean) {
        for (let s of this._sequences) {
            s.toggleShowDescriptionForSteps(show);
        }
    }

    toggleCollapsed(isCollapsed: boolean) {
        for (let s of this._sequences) {
            if (s._id !== FlowConstants.ERROR_SEQUENCE && s._id !== FlowConstants.END_SEQUENCE)
                s.isCollapsed = isCollapsed;
        }
        StateManager.propertyChanged(this, FlowConfiguration.COLLAPSED_CHANGED);
    }

    find(id: string): FlowStepData | FlowStepSequence | undefined {
        for (let s of [...this._sequences, this.errorSequence, this.endSequence]) {
            if (s._id === id) return s;

            let step = s.find(id);

            if (step) return step;
        }
        return undefined;
    }

    findSequenceByStepId(stepId: string): FlowStepSequence | undefined {
        for (let s of [...this._sequences, this.errorSequence, this.endSequence]) {
            if (s.find(stepId)) return s;
        }
        return undefined;
    }

    add(step: FlowStepData, seq: FlowStepSequence, index?: number, branch: number = 0): void {
        seq.addStep(step, index, branch);
    }

    remove(step: FlowStepData, sequenceId?: string): void {
        let seq: FlowStepSequence | undefined = undefined;
        if (sequenceId) {
            seq = this.getSequence(sequenceId);
        } else {
            seq = this.findSequenceByStepId(step._id);
        }

        if (seq) {
            seq.removeStep(step as FlowStepData);
        }
    }

    moveInSequence(
        step: FlowStepData,
        sequence: string,
        index: number,
        frombranch: number = 0,
        tobranch: number = 0,
    ): void {
        let s = this.getSequence(sequence);

        if (s) {
            s.moveStep(step, index, frombranch, tobranch);
        }
    }

    canMoveToSequence(step: FlowStepData, toSequence: IFlowStepSequence): boolean {
        let seqs = this.getUpstreamSequencesFromStep(step);
        return !(seqs.length > 0 && seqs.includes(toSequence));
    }

    moveToSequence(
        step: FlowStepData,
        fromSequence: string | FlowStepSequence,
        toSequence: string | FlowStepSequence,
        index?: number,
        toBranch: number = 0,
    ): void {
        let fromS = fromSequence instanceof FlowStepSequence ? fromSequence : this.getSequence(fromSequence);
        let toS = toSequence instanceof FlowStepSequence ? toSequence : this.getSequence(toSequence);

        if (!fromS || !toS) return;

        // check if sequence is connected to step.
        if (!this.canMoveToSequence(step, toS)) {
            throw new Error("You can not move this step");
        }

        fromS.removeStep(step);
        toS.addStep(step, index, toBranch);
    }

    getSequence(id: string): FlowStepSequence | undefined {
        for (let s of [...this._sequences, this.endSequence, this.errorSequence]) {
            if (s._id === id) return s;
        }
        return undefined;
    }

    addSequence(newSeq: FlowStepSequence) {
        this._sequences.push(newSeq);
        newSeq.flow = this;
        StateManager.propertyChanged(this, FlowConfiguration.ADD_SEQUENCE);
        StateManager.propertyChanged(this, FlowConfiguration.SEQUENCES);

        this.invalidateNodes();
        StateManager.addObserver(
            newSeq,
            () => {
                this.invalidateNodes();
            },
            undefined,
            "FLOW_LISTENER",
        );
    }

    deleteSequenceByIndex(idx: number) {
        this._sequences.splice(idx, 1);
        this.invalidateNodes();

        StateManager.propertyChanged(this, FlowConfiguration.REMOVE_SEQUENCE);
        StateManager.propertyChanged(this, FlowConfiguration.SEQUENCES);
    }

    getConnections(): FlowConnection[] {
        let conn: FlowConnection[] = [];

        for (let seq of this._sequences) {
            for (let step of seq.allSteps) {
                for (let inst of step.getOutcomeInstructions()) {
                    if (inst.strategy === FlowStepOutputInstructionType.BRANCH && inst.connectedSequenceId) {
                        let startId = FlowUtilities.IsEndStep(step) ? seq._id : inst.getElementId();
                        conn.push(new FlowConnection(startId, inst.connectedSequenceId, false, seq, inst, step));

                        let connectedSeq = this.getSequence(inst.connectedSequenceId);
                        if (
                            connectedSeq &&
                            connectedSeq.strategy === FlowSequenceCompleteStrategy.RETURN_TO_CALLING_STEP
                        ) {
                            conn.push(
                                new FlowConnection(
                                    inst.connectedSequenceId,
                                    startId,
                                    false,
                                    seq,
                                    inst,
                                    step,
                                    true,
                                    connectedSeq,
                                ),
                            );
                        }
                    }
                }
            }
        }

        return conn;
    }

    getUpstreamSequences(s: FlowStepSequence): IFlowStepSequence[] {
        let seqs: IFlowStepSequence[] = [];

        if (!s.instructions) return [];

        let insts = s.instructions.filter(
            (ins: FlowStepOutputInstructions) => ins.strategy === FlowStepOutputInstructionType.BRANCH,
        );
        if (insts.length) {
            insts.forEach((ins: FlowStepOutputInstructions) => {
                if (ins.connectedSequenceId) {
                    let ss = this.getCandidateOrRealSequence(ins.connectedSequenceId);
                    if (ss) {
                        seqs.push(ss);
                        if (ss instanceof FlowStepSequence) {
                            let upstreamSeqs = this.getUpstreamSequences(ss);
                            for (let us of upstreamSeqs) {
                                if (!seqs.includes(us)) {
                                    seqs.push(us);
                                }
                            }
                        }
                    }
                }
            });
        }
        return seqs;
    }

    getUpstreamSequencesFromStep(step: FlowStepData): IFlowStepSequence[] {
        let seqs: IFlowStepSequence[] = [];
        for (let inst of step.getOutcomeInstructions()) {
            if (inst.strategy === FlowStepOutputInstructionType.BRANCH) {
                if (inst.connectedSequenceId) {
                    let ss = this.getCandidateOrRealSequence(inst.connectedSequenceId);
                    if (ss) {
                        seqs.push(ss);

                        if (ss instanceof FlowStepSequence) {
                            seqs = [...seqs, ...this.getUpstreamSequences(ss)];
                        }
                    }
                }
            }
        }

        return seqs;
    }

    canLink(start: FlowStepSequence, end: FlowStepSequence): boolean {
        return !this.getUpstreamSequences(end).includes(start);
    }

    getCandidateOrRealSequence(id: string): IFlowStepSequence | undefined {
        for (let cs of this._candidateSequences) {
            if (cs._id === id) return cs;
        }

        for (let rs of [...this.allSequences, this.endSequence, this.errorSequence]) {
            if (rs._id === id) return rs;
        }

        return undefined;
    }

    getOutcomes(): MultiExecutableOutputDefinition[] {
        switch (this.flowDataOutputStrategy) {
            case FlowOutputDataStrategy.AllData:
                return [
                    new MultiExecutableOutputDefinition(
                        "complete",
                        FlowUtilities.getFlowDataDefinitions(this, FlowDataFetchType.All),
                    ),
                ];
            case FlowOutputDataStrategy.AddedData:
                return [
                    new MultiExecutableOutputDefinition(
                        "complete",
                        FlowUtilities.getFlowDataDefinitions(this, FlowDataFetchType.Added),
                    ),
                ];
            case FlowOutputDataStrategy.ChangedData:
                return [
                    new MultiExecutableOutputDefinition(
                        "complete",
                        FlowUtilities.getFlowDataDefinitions(this, FlowDataFetchType.Changed),
                    ),
                ];
            case FlowOutputDataStrategy.SpecifiedData:
                let allData = FlowUtilities.getFlowDataDefinitions(this, FlowDataFetchType.All);

                let output: IDataDefinition[] = [];

                if (this.flowOutputVariableNames) {
                    for (let d of this.flowOutputVariableNames) {
                        let ddd = DataUtilities.getFieldFromDataDefinitionByName(allData, d);
                        if (ddd) output.push(ddd);
                    }
                }

                return [new MultiExecutableOutputDefinition("complete", output)];
        }

        return [new MultiExecutableOutputDefinition("complete")];
    }

    getStorageData(): object {
        let data = super.getStorageData();

        Reflect.set(data, "_id", this._id);
        Reflect.set(
            data,
            "_sequences",
            this._sequences.map((s) => s.getStorageData()),
        );
        Reflect.set(data, "errorSequence", this.errorSequence.getStorageData());
        Reflect.set(data, "endSequence", this.endSequence.getStorageData());
        Reflect.set(data, "visibleSteps", this.visibleSteps);
        Reflect.set(data, "favoriteSteps", this.favoriteSteps);

        Reflect.set(data, "flowOutputAllData", this.flowDataOutputStrategy);
        Reflect.set(data, "flowOutputVariableNames", this.flowOutputVariableNames);

        Reflect.set(data, "outputType", this.defineOutputs);
        if (this._executionResult && this.canChangeOutputs) {
            Reflect.set(data, "executionResult", this.getExecutionResult().getStorageData());
        }

        Reflect.set(data, "mapping", this._outputMapping.getStorageData());

        return data;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        this._id = Reflect.get(data, "_id");
        this.visibleSteps = Reflect.get(data, "visibleSteps");
        this.favoriteSteps = Reflect.get(data, "favoriteSteps");
        this._sequences = Reflect.get(data, "_sequences").map((sequenceData: object) => {
            let s = new FlowStepSequence();
            s.setStorageData(sequenceData);

            return s;
        });

        this.endSequence = this._sequences.find((s) => {
            return s._id === FlowConstants.END_SEQUENCE;
        })!;

        this.errorSequence = this._sequences.find((s) => {
            return s._id === FlowConstants.ERROR_SEQUENCE;
        })!;

        this.flowDataOutputStrategy = Reflect.get(data, "flowOutputAllData");
        this.flowOutputVariableNames = Reflect.get(data, "flowOutputVariableNames");

        this.defineOutputs = Reflect.get(data, "outputType");
        let dd = Reflect.get(data, "executionResult");

        if (dd) {
            this._executionResult = new DataDefinition();
            this._executionResult.setStorageData(dd);
        }

        this._outputMapping = new MappingConfiguration();
        this._outputMapping.setStorageData(Reflect.get(data, "mapping"));
        this.invalidateNodes();
    }

    getDesignValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];
        for (let seq of this.allSequences) {
            issues.push(...seq.getValidationIssues());

            for (let step of seq.steps) {
                issues.push(...step.getValidationIssues());
            }
        }

        return issues;
    }
}
